// src/components/LogoUploadSection.tsx - NEW COMPONENT
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useThemeContext } from '../contexts/ThemeContext';

interface LogoUploadSectionProps {
  canEdit: boolean;
}

const LogoUploadSection: React.FC<LogoUploadSectionProps> = ({ canEdit }) => {
  const { company } = useAuth();
  const { mode } = useThemeContext();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(
    company?.logo || null
  );

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
  
    console.log('\n🟢 ========== LOGO UPLOAD STARTED ==========');
    console.log('📁 File selected:', file.name);
    console.log('   Type:', file.type);
    console.log('   Size:', file.size, 'bytes');
  
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      console.log('❌ Invalid file type');
      setError('Only JPEG, PNG, SVG, and WebP images are allowed');
      return;
    }
  
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.log('❌ File too large');
      setError('File size must be less than 5MB');
      return;
    }
  
    setError(null);
    setSuccess(null);
    setUploading(true);
  
    try {
      const formData = new FormData();
      formData.append('logo', file);
  
      const token = localStorage.getItem('token');
      const companyId = (company as any)?._id || (company as any)?.id;
  
      console.log('📤 Uploading to:', `/api/companies/${companyId}/logo`);
      console.log('🔑 Token exists:', !!token);
      console.log('🏢 Company ID:', companyId);
  
      const response = await axios.post(
        `/api/companies/${companyId}/logo`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
  
      console.log('📥 Response received:', response.data);
      console.log('   Success:', response.data.success);
      console.log('   Logo URL:', response.data.data?.logo);
  
      if (response.data.success) {
        const newLogoUrl = `${response.data.data.logo}${response.data.data.logo.includes('?') ? '&' : '?'}bust=${Date.now()}`;
        
        console.log('🖼️  Setting new logo preview:', newLogoUrl);
        console.log('📊 Current logoPreview:', logoPreview);
        
        setLogoPreview(newLogoUrl);
        setSuccess('Logo uploaded successfully!');
        
        console.log('💾 Updating localStorage');
        // Update company in localStorage
        const storedCompany = JSON.parse(localStorage.getItem('company') || '{}');
        console.log('   Old logo in storage:', storedCompany.logo);
        storedCompany.logo = response.data.data.logo;
        localStorage.setItem('company', JSON.stringify(storedCompany));
        console.log('   New logo in storage:', storedCompany.logo);
        
        console.log('🔄 Reloading page in 1.5 seconds...');
        // Reload to update layout
        setTimeout(() => window.location.reload(), 1500);
        
        console.log('✅ Upload complete!');
        console.log('🟢 ========== END LOGO UPLOAD ==========\n');
      }
    } catch (err: any) {
      console.error('❌ ========== LOGO UPLOAD ERROR ==========');
      console.error('Error:', err);
      console.error('Response:', err.response?.data);
      console.error('Status:', err.response?.status);
      console.error('❌ ========== END ERROR ==========\n');
      
      setError(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete the company logo and revert to default?')) {
      return;
    }

    setError(null);
    setSuccess(null);
    setDeleting(true);

    try {
      const token = localStorage.getItem('token');
      const companyId = (company as any)?._id || (company as any)?.id;

      const response = await axios.delete(
        `/api/companies/${companyId}/logo`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setLogoPreview(null);
        setSuccess('Logo deleted successfully - reverted to default');

        // Update company in localStorage
        const storedCompany = JSON.parse(localStorage.getItem('company') || '{}');
        storedCompany.logo = null;
        localStorage.setItem('company', JSON.stringify(storedCompany));

        // Reload to update layout
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (err: any) {
      console.error('Logo delete error:', err);
      setError(err.response?.data?.message || 'Failed to delete logo');
    } finally {
      setDeleting(false);
    }
  };

  const getDefaultLogo = () => {
    return mode === 'dark' ? '/Scetru-Logo-white.png' : '/Scetru-Logo-black.png';
  };

  const currentLogo = logoPreview || getDefaultLogo();

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Company Logo
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Upload your company logo. It will be displayed in the sidebar and replaces the default Scetru logo.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {!canEdit && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Only super administrators can upload company logos
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Logo Preview */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Avatar
              variant="rounded"
              sx={{
                width: 120,
                height: 120,
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'divider',
                p: 2,
              }}
            >
              <Box
                component="img"
                src={currentLogo}
                alt="Company Logo"
                key={currentLogo}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.src = getDefaultLogo();
                }}
              />
            </Avatar>
            <Typography variant="caption" color="text.secondary">
              {logoPreview ? 'Custom Logo' : 'Default Logo'}
            </Typography>
          </Box>

          {/* Upload Controls */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Recommended: PNG or SVG with transparent background
              <br />
              Maximum size: 5MB
              <br />
              Supported formats: JPEG, PNG, SVG, WebP
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                component="label"
                startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                disabled={!canEdit || uploading || deleting}
              >
                {uploading ? 'Uploading...' : 'Upload Logo'}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/jpg,image/png,image/svg+xml,image/webp"
                  onChange={handleFileSelect}
                  disabled={!canEdit || uploading}
                />
              </Button>

              {logoPreview && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                  onClick={handleDeleteLogo}
                  disabled={!canEdit || uploading || deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Logo'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LogoUploadSection;